import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/ProductCard';
import api, { formatPrice } from '../api';

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { products: wishlist } = useWishlist();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (hash === '#wishlist') setTimeout(() => document.getElementById('wishlist')?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [hash, user]);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get('/orders/mine').then(r => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  return (
    <main className="max-w-5xl mx-auto px-6 py-20">
      <div className="flex items-start justify-between mb-14">
        <div>
          <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-3">Your account</p>
          <h1 className="font-serif-display text-5xl mb-3">Hello, {user.name.split(' ')[0]}.</h1>
          <p className="text-[#1a1a1a]/70">{user.email}</p>
        </div>
        <button onClick={async () => { await logout(); navigate('/'); }} className="text-[11px] tracking-widest-plus uppercase link-underline">Sign out</button>
      </div>

      <section id="wishlist" data-testid="account-wishlist" className="mb-16 scroll-mt-40">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif-display text-3xl">Your wishlist</h2>
          {wishlist.length > 0 && <span className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50">{wishlist.length} saved</span>}
        </div>
        {wishlist.length === 0 ? (
          <div className="border border-black/10 p-10 text-center">
            <Heart className="w-6 h-6 mx-auto mb-3 text-[#1a1a1a]/40" />
            <p className="text-[#1a1a1a]/70 mb-4">Nothing saved yet. Tap the heart on any piece to keep it here.</p>
            <Link to="/shop" className="btn-ghost">Browse the collection</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {wishlist.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      <h2 className="font-serif-display text-3xl mb-8">Your orders</h2>
      {orders.length === 0 ? (
        <div className="border border-black/10 p-10 text-center">
          <p className="text-[#1a1a1a]/70 mb-4">You have not placed any orders yet.</p>
          <Link to="/shop" className="btn-primary">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map(o => (
            <div key={o.id} className="border border-black/10 p-6">
              <div className="flex justify-between mb-4">
                <div>
                  <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60">Order</p>
                  <p className="font-serif-display text-xl">{o.id}</p>
                </div>
                <span className="text-[11px] tracking-widest-plus uppercase bg-[#E9E1D4] px-3 py-1 h-fit">{o.status.replace('_', ' ')}</span>
              </div>
              <ul className="text-sm mb-4 space-y-1">
                {o.items.map((it, i) => <li key={i}>{it.name} × {it.qty}</li>)}
              </ul>
              <p className="text-sm">Total: <span className="font-medium">{formatPrice(o.total)}</span></p>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
